import { useState } from "react";

// Backend URL — resolved at RUNTIME (not build time) so a restart-without-
// env-vars doesn't quietly fall back to a bad hostname:
//   1. process.env.REACT_APP_RCS_BOT_URL wins if the build had it set
//   2. otherwise: dev builds on localhost auto-target the local backend
//      on :9500 so no env vars are needed to get things working locally
//   3. prod builds default to the prod hostname
// Joseph 2026-07-24: "solve it permanently" — this fixes the
// "Failed to fetch" that appeared whenever npm start was restarted
// without the env-var line.
function resolveRcsUrl(): string {
  // URL sources — first hit wins. NO hardcoded prod fallback
  // (Joseph 2026-07-27: "no urls in codebase, only in env"). If none
  // of these resolve, calls will fail with a clear error banner
  // instead of silently hitting a wrong host.
  //
  // 1) Runtime override — inline <script>window.__RCS_BOT_URL__="…"</script>
  //    in index.html before the bundle loads (lets ops change the URL
  //    without a rebuild).
  if (typeof window !== "undefined") {
    const runtime = (window as unknown as { __RCS_BOT_URL__?: string }).__RCS_BOT_URL__;
    if (typeof runtime === "string" && runtime) return runtime;
  }
  // 2) Build-time env — CRA bakes this at build. Set in the build
  //    pipeline's env or a .env file next to package.json.
  const fromEnv = process.env.REACT_APP_RCS_BOT_URL;
  if (fromEnv) return fromEnv;
  // 3) Localhost dev auto-target only (Docker Desktop convenience).
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      return "http://localhost:9500";
    }
    // 4) Prod hostname fallback — derive the proxy path from the current
    //    hostname when the frontend is served from a *.connect.dalmlabs.com
    //    host. The bot service is exposed on journey_builder as /rcs-bot/*.
    //    Belt-and-braces so a missing index.html override or unset env
    //    still hits the right backend instead of returning "" and
    //    generating relative-URL 404s (Eben 2026-07-28 publish 404).
    if (host.endsWith(".connect.dalmlabs.com") || host === "connect.dalmlabs.com") {
      return "https://journeybuilder.connect.dalmlabs.com/rcs-bot";
    }
  }
  // 5) No URL configured — return empty string. All fetches will
  //    fail fast; the circuit-breaker banner surfaces the config
  //    problem to the operator.
  return "";
}
const baseURL = resolveRcsUrl();

// Auth: prefer the user's platform login JWT (Authorization: Bearer)
// because that's what every other endpoint on the platform already
// uses — no separate secret to bake into the frontend build. The
// rcs_bot_service accepts this token and derives tenant_id from its
// claims. Falls back to X-Service-Token if REACT_APP_RCS_BOT_TOKEN or
// localhost dev is set, purely for scripts / service-to-service calls.
// Joseph 2026-07-29: "stick to the normal login token".
function resolveServiceToken(): string {
  const fromEnv = process.env.REACT_APP_RCS_BOT_TOKEN;
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      // Local dev fallback — matches services/rcs_bot_service .env
      // RCS_BOT_SERVICE_TOKEN. Not sensitive.
      return "c489277ec70fc7be6fa3d1080e36675161439d8cb55a35a2e8306a0581020f0b";
    }
  }
  return "";
}
const SERVICE_TOKEN = resolveServiceToken();

// The user's platform JWT lives in localStorage under "authToken" —
// same key every other hook (bot-req, req.tsx) reads it from. We
// re-read at request time so a fresh login is picked up without a
// page reload.
function getAuthToken(): string {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem("authToken") || "";
  } catch {
    return "";
  }
}

export default function useRcsBotReq(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  headers?: Record<string, any>
) {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>();

  async function makeRequest(
    data?: any,
    params?: Record<string, any>,
    pathParam?: string
  ) {
    setLoading(true);

    const queryParams = new URLSearchParams(params || {}).toString();
    const urlWithParams =
      pathParam && queryParams
        ? `${baseURL}${endpoint}/${pathParam}?${queryParams}`
        : pathParam
          ? `${baseURL}${endpoint}/${pathParam}`
          : queryParams
            ? `${baseURL}${endpoint}?${queryParams}`
            : `${baseURL}${endpoint}`;

    // Prefer the user's platform login JWT. Bearer wins over X-Service-Token;
    // service token is only sent if no Bearer is available (dev / scripts).
    const authToken = getAuthToken();
    const authHeaders: Record<string, string> = authToken
      ? { Authorization: `Bearer ${authToken}` }
      : SERVICE_TOKEN
        ? { "X-Service-Token": SERVICE_TOKEN }
        : {};

    try {
      const res = await fetch(urlWithParams, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
          ...headers,
        },
        body:
          ["POST", "PUT", "DELETE", "PATCH"].includes(method) && data
            ? JSON.stringify(data)
            : undefined,
      });

      let json: any = {};
      try {
        json = await res.json();
      } catch {
        // Non-JSON body (e.g. empty 204) — leave json as {}
      }

      setResponse(json);
      setLoading(false);
      return [json, res.status];
    } catch (error: any) {
      setLoading(false);
      return [{ detail: error?.message || "Request failed" }, 500];
    }
  }

  return { loading, makeRequest, response };
}